import { api_configuration } from "../../common";
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_client_state = api_configuration.types.client_state.alan_api;
import DestructorTree from "../DestructorTree";
import { visit_widget_implementation } from "./visit_widget_implementation";
import { wrapNode } from "./wrapNode";
import { iReference } from "../state/Reference";
import * as Rule from "../state/Rule";
import {
	registerGuiWidgetConfigurationNode,
	registerGroup,
	registerStategroup,
	registerText,
	registerNodeContext,
} from "../state/registerClientState";
import { Result, isResolved } from "../Status";
import { Scalar, onScalarValue } from "../../../../../stream";

export function visit_widget_expression(
	expression: api_views.Cwidget_expression,
	current_context: api_client_state.AlanObject,
	destructor: DestructorTree,
	implementation_arguments: ImplementationArguments,
	wrap_in_dynamic_area = false
): void {
	switch (expression.properties.type.state.name) {
		case "group": {
			const group_type = expression.properties.type.state.node;
			const node = current_context as unknown as api_client_state.Cnode;
			const group_property = registerGroup(group_type.properties.group.ref.inferences.group(), node, destructor);
			visit_widget_expression(
				group_type.properties.next,
				group_property.properties.node,
				destructor,
				implementation_arguments,
				wrap_in_dynamic_area
			);
			break;
		}
		case "state": {
			const state_type = expression.properties.type.state.node;
			const node = current_context as unknown as api_client_state.Cnode;
			const state = registerStategroup(
				state_type.properties.state_group.ref.inferences.state_group(),
				node,
				destructor
			).properties.states.get(state_type.properties.state.entry)!;
			bindToState(state, state_type.properties.next, destructor, implementation_arguments);
			break;
		}
		case "switch": {
			const switch_type = expression.properties.type.state.node;
			const node = current_context as unknown as api_client_state.Cnode;
			const state_group_property = registerStategroup(
				switch_type.properties.state_group.ref.inferences.state_group(),
				node,
				destructor
			);
			for (const state of switch_type.properties.states.values()) {
				const client_state = state_group_property.properties.states.get(state.key.entry)!;
				bindToState(client_state, state.properties.next, destructor, implementation_arguments);
			}
			break;
		}
		case "parent": {
			const parent_type = expression.properties.type.state.node;
			const node = current_context as unknown as api_client_state.Cnode;
			const parent = node.input
				.position()
				.input.parent()
				.switch({
					node: ($) => $,
					none: node.input
						.position()
						.input.root()
						.switch({
							root: ($) => $.properties.root,
							operation: ($) => $.parent.parent.parent,
						}),
				});
			visit_widget_expression(
				parent_type.properties.next,
				parent,
				destructor,
				implementation_arguments,
				wrap_in_dynamic_area
			);
			break;
		}
		case "widget": {
			const widget_type = expression.properties.type.state.node;
			if (current_context instanceof api_client_state.Cnode) {
				registerGuiWidgetConfigurationNode(
					widget_type.properties.widget.properties.configuration,
					current_context,
					destructor
				);
			}
			visit_widget_implementation(
				wrap_in_dynamic_area
					? wrapNode(implementation_arguments.parent_dom_element, destructor)
					: implementation_arguments.parent_dom_element,
				widget_type.properties.widget.properties.widget.ref,
				widget_type.properties.widget.properties.configuration,
				current_context,
				destructor,
				implementation_arguments.view_root_dom_element,
				implementation_arguments.client_view,
				implementation_arguments.model_binding,
				implementation_arguments.entry_keys
			);
			break;
		}
		case "view": {
			const view_type = expression.properties.type.state.node;
			visit_widget_expression(
				view_type.properties.view.ref.properties.instance,
				current_context,
				destructor,
				implementation_arguments,
				wrap_in_dynamic_area
			);
			break;
		}
		case "reference": {
			const reference_type = expression.properties.type.state.node;
			const node = current_context as unknown as api_client_state.Cnode;
			const text = registerText(reference_type.properties.text.ref.inferences.text(), node, destructor);
			const reference = text.properties.navigable.cast("yes");
			bindToNodeStream(
				iReference(reference).referencedNode,
				reference_type.properties.next,
				destructor,
				implementation_arguments
			);
			break;
		}
		case "reference rule": {
			const reference_rule_type = expression.properties.type.state.node;
			const node = current_context as unknown as api_client_state.Cnode;
			const text = registerText(reference_rule_type.properties.text.ref.inferences.text(), node, destructor);
			const rule_type = reference_rule_type.properties.rule.ref.key.ref;
			const rule = text.properties.navigable.cast("yes").properties.rules.properties.rules.get(rule_type.key)!;
			bindToNodeStream(Rule.node(rule), reference_rule_type.properties.next, destructor, implementation_arguments);
			break;
		}
		case "state context rule": {
			const state_context_rule_type = expression.properties.type.state.node;
			const rule_type = state_context_rule_type.properties.rule.ref.key.ref;
			const node = current_context as unknown as api_client_state.Cnode;
			const state = node.input.position().cast("state");
			const rule = state.properties.rules.properties.rules.get(rule_type.key)!;
			bindToNodeStream(Rule.node(rule), state_context_rule_type.properties.next, destructor, implementation_arguments);
			break;
		}
		default:
			unexpectedState(expression.properties.type.state);
	}
}

function bindToState(
	state: api_client_state.Cstates__state_group__type__property,
	expression: api_views.Cwidget_expression,
	destructor: DestructorTree,
	implementation_arguments: ImplementationArguments
) {
	destructor.subscribeToStategroup(state.properties.is_selected, (state_destructor) => {
		switch (state.properties.is_selected.state.name) {
			case "no":
				break;
			case "yes": {
				visit_widget_expression(expression, state.properties.node, state_destructor, implementation_arguments, true);
				break;
			}
			default:
				unexpectedState(state.properties.is_selected.state);
		}
	});
}

function bindToNodeStream(
	stream: Scalar<Result<api_client_state.Cnode>>,
	expression: api_views.Cwidget_expression,
	destructor: DestructorTree,
	implementation_arguments: ImplementationArguments
) {
	let sub_destructor: DestructorTree | undefined;
	onScalarValue(
		stream,
		(node) => {
			sub_destructor?.destroy();
			if (isResolved(node)) {
				sub_destructor = destructor.addChild();
				registerNodeContext(node, sub_destructor);
				visit_widget_expression(expression, node, sub_destructor, implementation_arguments, true);
			}
		},
		(scalar, subscription) => destructor.onDestroy(() => scalar.unsubscribe(subscription))
	);
}

type ImplementationArguments = {
	parent_dom_element: () => HTMLElement;
	view_root_dom_element: () => HTMLElement;
	client_view: api_client_state.Copen_views;
	model_binding: api_views.Cmodel_binding | undefined;
	entry_keys: string[];
};
